import type { PortalAnnouncement } from "@nodus/announcements";

/**
 * Announcement audio for the member portal — the pure half (no React, no DOM,
 * no storage), so the scheduling / dedup / mute rules are unit-testable.
 *
 * "Same audio for all places": the portal plays the announcement's OWN
 * ``AudioAsset`` — the clip and schedule the meeting-room kiosks play
 * (``launcher-frontend/src/hooks/useScheduledAudio.ts``). The backend ships it
 * as ``audio`` on ``GET /api/tenant-portal/announcements/``.
 *
 * **Which announcements play:** any active, visible announcement that has a
 * file-backed audio clip — whatever its category. Emergencies without an
 * attached clip stay silent: the portal never invents a sound the kiosks
 * would not play. ``preset`` clips have no file (kiosks resolve the slug
 * locally) and arrive as ``audio: null``, so they are silent here too.
 */

/** The ``audio`` block of a portal announcement (``PortalAnnouncementAudioResponse``). */
export interface PortalAnnouncementAudio {
  /** Presigned storage URL — fresh on every fetch, so it changes per refetch. */
  url: string;
  duration_ms: number | null;
  /** Wall-clock moment the first play is scheduled for (kiosk-synchronised). */
  play_at: string | null;
  /** ``null`` = play once; ``0`` = back-to-back loop; ``N`` = every N seconds. */
  repeat_interval_seconds: number | null;
  /** Total plays; ``null`` with an interval = until cleared. */
  repeat_count: number | null;
}

/** ``PortalAnnouncement`` plus the audio block. Kept local (not in
 *  ``@nodus/announcements``) so the shared, design-synced package is untouched;
 *  the backend field is optional-by-absence for older payloads. */
export type AnnouncementWithAudio = PortalAnnouncement & { audio?: PortalAnnouncementAudio | null };

/** Same stale guard as the kiosk: a single-play clip whose moment passed more
 *  than this long ago is skipped (don't honk hours later on page load). */
export const STALE_PLAY_AT_MS = 30_000;

/** Cap on remembered mutes so localStorage can't grow without bound. */
export const MAX_MUTED_KEYS = 100;

export function hasAudio(a: AnnouncementWithAudio): a is AnnouncementWithAudio & { audio: PortalAnnouncementAudio } {
  return !!a.audio?.url;
}

/**
 * Identity of one *raising* of an announcement's sound. The presigned URL
 * changes on every refetch, so it must NOT be part of the key — otherwise
 * every 30 s poll / realtime ping would restart playback. ``raised_at`` +
 * ``play_at`` change when the operator re-raises / reactivates the row, so a
 * mute (or a finished play) doesn't swallow a genuinely new alarm.
 */
export function announcementAudioKey(a: AnnouncementWithAudio): string {
  return `${a.id}|${a.raised_at}|${a.audio?.play_at ?? ""}`;
}

/** Should this announcement's audio be playing right now in this browser? */
export function shouldPlayAnnouncementAudio(
  a: AnnouncementWithAudio,
  state: { enabled: boolean; mutedKeys: ReadonlySet<string> },
): boolean {
  return state.enabled && hasAudio(a) && !state.mutedKeys.has(announcementAudioKey(a));
}

/** Add a mute (most-recent last), deduped and capped to ``cap`` entries. */
export function addMutedKey(list: readonly string[], key: string, cap = MAX_MUTED_KEYS): string[] {
  const next = list.filter((k) => k !== key);
  next.push(key);
  return next.length > cap ? next.slice(next.length - cap) : next;
}

export function removeMutedKey(list: readonly string[], key: string): string[] {
  return list.filter((k) => k !== key);
}

/** Parse the persisted mute list; anything malformed reads as "nothing muted". */
export function parseMutedKeys(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === "string") : [];
  } catch {
    return [];
  }
}

/** What the player should do for one announcement, computed once at mount. */
export type AudioPlan =
  | { kind: "skip" }
  | {
      kind: "play";
      /** Wait this long before the first play. */
      delayMs: number;
      /** Plays to perform from then on (``Infinity`` = until stopped). */
      plays: number;
      /** ``true`` = chain on the ``ended`` event (interval 0, back-to-back). */
      chain: boolean;
      /** Gap between plays when not chaining. */
      intervalMs: number;
    };

/**
 * Mirror of the kiosk's ``useScheduledAudio`` scheduling, as a pure function.
 *
 * - interval ``null`` ⇒ play once at the anchor; skipped if the anchor is more
 *   than ``STALE_PLAY_AT_MS`` in the past (a reload doesn't replay it).
 * - interval ``0`` ⇒ back-to-back via ``ended``, ``repeat_count`` plays
 *   (``null`` = until stopped). Past the anchor it just starts now.
 * - interval ``N`` ⇒ plays at ``anchor + k·N``. Past the anchor it resumes
 *   mid-cycle at the next slot with the remaining count, or skips once the
 *   schedule is exhausted — so a refresh / navigation / resume picks up where
 *   the kiosks are instead of starting over.
 *
 * The anchor is ``play_at``, falling back to ``raised_at`` (a row the kiosk
 * fan-out never stamped) so a reload can't replay an old single-play clip.
 */
export function planAnnouncementAudio(args: {
  anchor: string | null;
  now: number;
  repeatIntervalSeconds: number | null;
  repeatCount: number | null;
}): AudioPlan {
  const { now } = args;
  const interval = args.repeatIntervalSeconds;
  const total = args.repeatCount === null ? Infinity : Math.max(1, args.repeatCount);
  const anchorMs = args.anchor ? new Date(args.anchor).getTime() : NaN;
  const elapsed = Number.isFinite(anchorMs) ? now - anchorMs : 0;

  if (interval === null || interval < 0) {
    if (elapsed >= STALE_PLAY_AT_MS) return { kind: "skip" };
    return { kind: "play", delayMs: Math.max(0, -elapsed), plays: 1, chain: false, intervalMs: 0 };
  }
  if (interval === 0) {
    return { kind: "play", delayMs: Math.max(0, -elapsed), plays: total, chain: true, intervalMs: 0 };
  }
  const intervalMs = interval * 1000;
  if (elapsed <= 0) {
    return { kind: "play", delayMs: -elapsed, plays: total, chain: false, intervalMs };
  }
  const playsDone = Math.floor(elapsed / intervalMs) + 1;
  if (playsDone >= total) return { kind: "skip" };
  return {
    kind: "play",
    delayMs: intervalMs - (elapsed % intervalMs),
    plays: total - playsDone,
    chain: false,
    intervalMs,
  };
}
